import React, { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, Textarea, ConfirmModal, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { createChangeOrder, updateChangeOrder } from '../services/api';

import { PMChangeOrder } from '../types/pm';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface FormData {
  title: string;
  amount: number | '';
  estimatedHours: number | '';
  description: string;
}

/** 新增／編輯變更單（照 PaymentDialog）：標題、金額、預估工時、說明 */
const ChangeOrderDialog: React.FC<{ projectId: string; order?: PMChangeOrder; onClose: () => void; onSaved: (order: PMChangeOrder) => void }> = ({
  projectId,
  order,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(['translation', 'pages']);
  const { register, handleSubmit, formState: { errors, isDirty }, reset } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const amountInput = useNumberInput('integer');
  const hoursInput = useNumberInput('decimal');
  const isEditMode = !!order;

  const actualHandleClose = () => { reset(); onClose(); };
  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  useLayoutEffect(() => {
    reset(order
      ? { title: order.title, amount: order.amount, estimatedHours: order.estimatedHours ?? '', description: order.description || '' }
      : { title: '', amount: '', estimatedHours: '', description: '' });
  }, [order, reset]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const payload = {
        title: data.title.trim(),
        amount: Number(data.amount) || 0,
        estimatedHours: data.estimatedHours === '' ? null : Number(data.estimatedHours),
        description: data.description.trim() || null,
      };
      const { data: saved } = isEditMode ? await updateChangeOrder(order.id, payload) : await createChangeOrder(projectId, payload);
      toast.success(t(isEditMode ? 'common.messages.updateSuccess' : 'common.messages.createSuccess'));
      onSaved(saved);
      actualHandleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('common.messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={loading} className="max-w-lg">
        <DialogHeader
          title={t(isEditMode ? 'pmChangeOrders.editTitle' : 'pmChangeOrders.addTitle', { ns: 'pages' })}
          onClose={handleClose}
          closeDisabled={loading}
          closeLabel={t('common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            <FormFieldWithError label={t('pmChangeOrders.fields.title', { ns: 'pages' })} required error={errors.title?.message} htmlFor="title">
              <Input
                id="title"
                {...register('title', {
                  required: t('pmChangeOrders.validation.titleRequired', { ns: 'pages' }),
                  maxLength: { value: 200, message: t('common.forms.validation.maxLength', { length: 200 }) },
                })}
                variant={errors.title ? 'error' : 'default'}
                placeholder={t('pmChangeOrders.placeholders.title', { ns: 'pages' })}
              />
            </FormFieldWithError>
            <div className="grid grid-cols-2 gap-4">
              <FormFieldWithError label={t('pmChangeOrders.fields.amount', { ns: 'pages' })} required error={errors.amount?.message} htmlFor="amount">
                <Input
                  type="text"
                  id="amount"
                  {...amountInput.bind(register('amount', { required: t('pmChangeOrders.validation.amountRequired', { ns: 'pages' }), ...amountInput.validationRules }))}
                  variant={errors.amount ? 'error' : 'default'}
                  placeholder="0"
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('pmChangeOrders.fields.estimatedHours', { ns: 'pages' })} error={errors.estimatedHours?.message} htmlFor="estimatedHours">
                <Input type="text" id="estimatedHours" {...hoursInput.bind(register('estimatedHours', hoursInput.validationRules))} placeholder="0" />
              </FormFieldWithError>
            </div>
            <FormFieldWithError label={t('pmChangeOrders.fields.description', { ns: 'pages' })} error={errors.description?.message} htmlFor="description">
              <Textarea
                id="description"
                rows={4}
                {...register('description', { maxLength: { value: 2000, message: t('common.forms.validation.maxLength', { length: 2000 }) } })}
                placeholder={t('pmChangeOrders.placeholders.description', { ns: 'pages' })}
              />
            </FormFieldWithError>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={loading} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton
              type="submit"
              loading={loading}
              isEditMode={isEditMode}
              customText={{ create: t('common.actions.create'), createLoading: t('common.status.creating') }}
              t={t}
            />
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('components.modal.unsavedChanges.title')}
        message={t('components.modal.unsavedChanges.message')}
        confirmText={t('components.modal.unsavedChanges.confirmButton')}
        cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default ChangeOrderDialog;
